import { useState, useEffect, lazy, Suspense } from 'react';
import { RefreshCw } from 'lucide-react';
import AdminLogin from './AdminLogin';

// Lazy load the actual admin dashboard - only loads after authentication
const AdminDashboard = lazy(() => import('./AdminDashboard'));

export default function AdminWrapper() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const sessionToken = localStorage.getItem('admin_session_token');

    if (!sessionToken) {
      setIsCheckingAuth(false);
      return;
    }

    try {
      const azureFunctionsUrl = import.meta.env.VITE_AZURE_FUNCTIONS_URL || 'http://localhost:7071/api';
      const response = await fetch(`${azureFunctionsUrl}/auth/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionToken }),
      });

      const data = await response.json();

      if (data.valid) {
        setIsAuthenticated(true);
      } else {
        localStorage.removeItem('admin_session_token');
      }
    } catch (error) {
      console.error('Error validating session:', error);
      localStorage.removeItem('admin_session_token');
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Validating session...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Only NOW do we lazy load the actual admin dashboard
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green mb-4"></div>
            <p className="text-xl text-gray-600">Loading admin dashboard...</p>
          </div>
        </div>
      }
    >
      <AdminDashboard />
    </Suspense>
  );
}
